import { ProductTableType, type NpiTabContext } from "~/constants/productTable"
import { MultiEditSkuPanel } from "../MultiEditSkuPanel"
import { NoosCommandBar } from "~/components/noos-supply-plan/NoosCommandBar"
import { NoosBuyActions } from "~/components/noos-supply-plan/NoosBuyActions"
import { NoosVariantCommandBar } from "~/components/noos-supply-plan/NoosVariantCommandBar"
import { MultiEditNpiPanel } from "../MultiEditNpiPanel"
import { MultiEditSkuSettingsPanel } from "../MultiEditSkuSettingsPanel"
import { MultiEditSkuCollectionPanel } from "~/components/collections/MultiEditSkuCollectionPanel"
import { MultiEditVariantPurchasePlanPanel } from "~/components/purchase-plan/MultiEditVariantPurchasePlanPanel"
import { MultiEditSeasonalBuysPanel } from "../MultiEditSeasonalBuysPanel"
import { MultiEditReplenishmentSuggestionsPanel } from "../MultiEditReplenishmentSuggestionsPanel"
import { memo, useEffect, useState } from "react"
import {
  useSelectedVariantHandlers,
  useSelectedVariantState,
} from "~/context/SelectedVariantsProvider"
import { MultiEditInactiveSkuSettingsPanel } from "~/components/reusable/multiEditPanel/MultiEditInactiveSkuSettings"
import { ProductManagementCommandBar } from "~/components/npi/ProductManagementCommandBar"
import { NoosDemandPlanCommandBar } from "~/components/noos-supply-plan/NoosDemandPlanCommandBar"
import { ReviewStatusButtons } from "~/components/forecast/ReviewStatusButtons"
import { Demand2CommandBar } from "~/components/demand-2/Demand2CommandBar"

// 1. Define Props Interface
type MultiEditPanelProps = {
  type: ProductTableType
  collectionIds?: number[]
  npiContext?: "suggested"
  tabContext?: NpiTabContext
  hideReorder?: boolean
  hideAdjust?: boolean
  delayedPoWarningMap?: Map<
    string,
    {
      poName: string
      poExpectedArrivalDate: Date
      allPos: {
        poName: string
        warehouseId: string
        warehouseName: string
        poExpectedArrivalDate: Date
      }[]
    }
  >
  snoozedUntilMap?: Map<string, Date>
  onReviewSelected?: (variantIds: string[]) => void
}

// 2. Define the Component as a standard Generic function first
const MultiEditPanelInner = ({
  type,
  collectionIds,
  npiContext,
  tabContext,
  hideReorder,
  hideAdjust,
  delayedPoWarningMap,
  snoozedUntilMap,
  onReviewSelected,
}: MultiEditPanelProps) => {
  const { selectedVariantCount } = useSelectedVariantState()
  const { getSelectedVariantIds, clearSelectedVariants } = useSelectedVariantHandlers()

  const [selectedVariantIds, setSelectedVariantIds] = useState(getSelectedVariantIds())

  useEffect(() => {
    setSelectedVariantIds(getSelectedVariantIds())
  }, [getSelectedVariantIds, selectedVariantCount])

  if (selectedVariantCount === 0) return null

  switch (type) {
    case ProductTableType.Default:
      return (
        <MultiEditSkuPanel
          selectedVariantIds={selectedVariantIds}
          selectedVariantCount={selectedVariantCount}
          clearSelectedVariantIds={clearSelectedVariants}
          collectionId={collectionIds?.[0]}
          hideReorder={hideReorder}
          hideAdjust={hideAdjust}
        />
      )
    case ProductTableType.NPI:
      return (
        <MultiEditNpiPanel
          selectedVariantIds={selectedVariantIds}
          selectedVariantCount={selectedVariantCount}
          clearSelectedVariantIds={clearSelectedVariants}
          npiContext={npiContext}
        />
      )
    case ProductTableType.Demand2:
      return (
        <Demand2CommandBar
          selectedVariantIds={selectedVariantIds}
          selectedVariantCount={selectedVariantCount}
          clearSelectedIds={clearSelectedVariants}
        />
      )
    case ProductTableType.Settings:
      return (
        <MultiEditSkuSettingsPanel
          selectedVariantIds={selectedVariantIds}
          selectedVariantCount={selectedVariantCount}
          clearSelectedIds={clearSelectedVariants}
        />
      )
    case ProductTableType.Collection:
      return (
        <MultiEditSkuCollectionPanel
          selectedVariantIds={selectedVariantIds}
          selectedVariantCount={selectedVariantCount}
          clearSelectedIds={clearSelectedVariants}
          collectionIds={collectionIds}
        />
      )
    case ProductTableType.PurchasePlan:
      return (
        <MultiEditVariantPurchasePlanPanel
          selectedVariantIds={selectedVariantIds}
          selectedVariantCount={selectedVariantCount}
          clearSelectedIds={clearSelectedVariants}
        />
      )
    case ProductTableType.SeasonalBuys:
      return (
        <MultiEditSeasonalBuysPanel
          selectedVariantIds={selectedVariantIds}
          selectedVariantCount={selectedVariantCount}
          clearSelectedIds={clearSelectedVariants}
        />
      )
    case ProductTableType.ReplenishmentSuggestions:
      return (
        <MultiEditReplenishmentSuggestionsPanel
          selectedVariantIds={selectedVariantIds}
          selectedVariantCount={selectedVariantCount}
          clearSelectedIds={clearSelectedVariants}
        />
      )
    case ProductTableType.HiddenSKUs:
      return (
        <MultiEditInactiveSkuSettingsPanel
          selectedVariantCount={selectedVariantCount}
          clearSelectedSkuIds={clearSelectedVariants}
        />
      )
    case ProductTableType.ProductManagement:
      return tabContext ? (
        <ProductManagementCommandBar
          selectedVariantIds={selectedVariantIds}
          selectedVariantCount={selectedVariantCount}
          clearSelectedVariantIds={clearSelectedVariants}
          tabContext={tabContext}
        />
      ) : null
    case ProductTableType.NoosOverview:
      return (
        <NoosCommandBar
          selectedVariantIds={selectedVariantIds}
          selectedVariantCount={selectedVariantCount}
          clearSelectedVariants={clearSelectedVariants}
          delayedPoWarningMap={delayedPoWarningMap}
          snoozedUntilMap={snoozedUntilMap}
        />
      )
    case ProductTableType.NoosPlanning:
      return (
        <NoosVariantCommandBar
          selectedVariantIds={selectedVariantIds}
          selectedVariantCount={selectedVariantCount}
          clearSelectedVariantIds={clearSelectedVariants}
          collectionId={collectionIds?.[0]}
          hideReorder={hideReorder}
          hideAdjust={hideAdjust}
        />
      )
    case ProductTableType.NoosAvailability:
      return (
        <NoosVariantCommandBar
          selectedVariantIds={selectedVariantIds}
          selectedVariantCount={selectedVariantCount}
          clearSelectedVariantIds={clearSelectedVariants}
          hideReorder
          hideAdjust
          extraActions={
            <NoosBuyActions
              selectedVariantIds={selectedVariantIds}
              clearSelectedVariants={clearSelectedVariants}
            />
          }
        />
      )
    case ProductTableType.NoosDemandPlan:
      return (
        <NoosDemandPlanCommandBar
          selectedVariantIds={selectedVariantIds}
          selectedVariantCount={selectedVariantCount}
          clearSelectedVariantIds={clearSelectedVariants}
        />
      )
    case ProductTableType.ForecastUpdates:
      return (
        <MultiEditSkuPanel
          selectedVariantIds={selectedVariantIds}
          selectedVariantCount={selectedVariantCount}
          clearSelectedVariantIds={clearSelectedVariants}
          extraActions={
            <ReviewStatusButtons
              selectedVariantIds={selectedVariantIds}
              clearSelected={clearSelectedVariants}
              onReviewSelected={onReviewSelected}
            />
          }
        />
      )
    case ProductTableType.None:
      return null
    default:
      return null
  }
}

export const MultiEditPanel = memo(MultiEditPanelInner)
MultiEditPanel.displayName = "MultiEditPanel"
